import { ProviderData } from "@/types/provider";
import { 
  Building2, 
  Clock, 
  Percent, 
  CheckCircle2,
  ShieldCheck,
  Zap
} from "lucide-react";
import lanekollLogo from "@/assets/lanekoll-logo-new.webp";
import ucLogo from "@/assets/UC.png";
import bankTransferIcon from "@/assets/banköverföring.png";
import creditsafeImg from "@/assets/creditsafe.png";

export const lanekollData: ProviderData = {
  id: "lanekoll",
  name: "Lånekoll",
  slug: "lanekoll",
  logo: lanekollLogo,
  logoSize: "small",
  type: "comparison",
  numberOfLenders: 20,
  
  trustpilot: {
    rating: 4.9,
    reviews: 1800,
    url: "https://se.trustpilot.com/review/lanekoll.se"
  },
  
  hero: {
    badges: [
      { text: "Jämför 20 långivare med en ansökan", variant: "default" }
    ],
    description: "Lånekoll hjälper dig att hitta rätt privatlån, oavsett om du behöver ett nytt lån eller vill samla dina befintliga. Med endast en ansökan kan du jämföra privatlån från upp till 20 långivare och få full överblick i deras smarta app. Lånekoll kan även hjälpa dig med bolån, från att skaffa lånelöfte till att förhandla nya villkor eller omförhandla ditt nuvarande lån.",
    ctaText: "Ansök hos Lånekoll",
    ctaButtonText: "Ansök hos Lånekoll",
    ctaButtonVariant: "default"
  },
  
  stats: [
    {
      icon: Building2,
      value: "20 långivare",
      label: "Samarbetspartners",
      description: "Jämför erbjudanden från 20 olika långivare"
    },
    {
      icon: Zap,
      value: "600 000 kr",
      label: "Maxbelopp",
      description: "från 10 000 kr."
    },
    {
      icon: Percent,
      value: "5,33 – 33,83 %",
      label: "Effektiv ränta",
      description: "Varierar mellan långivare"
    },
    {
      icon: Clock,
      value: "1 – 15 år",
      label: "Löptid",
      description: "Flexibla återbetalningsvillkor"
    },
    {
      image: ucLogo,
      value: "UC",
      label: "Kreditupplysning",
      description: "Endast 1 UC-kreditupplysning"
    },
    {
      icon: CheckCircle2,
      value: "Ja",
      label: "Betalningsanmärkningar",
      description: "Vissa långivare accepterar"
    }
  ],
  
  features: {
    pros: [
      "Smart app för bättre kontroll",
      "Säker & omtyckt tjänst"
    ],
    cons: [
      "Utbetalning kan ta upp till 5 dagar"
    ]
  },
  
  keyInformation: [
    {
      icon: Building2,
      title: "20 Långivare",
      value: "Jämför erbjudanden från Sveriges ledande banker och finansbolag",
      description: "Få konkurrenskraftiga räntor genom bred jämförelse"
    },
    {
      image: ucLogo,
      title: "En UC-kreditupplysning",
      value: "Endast en kreditupplysning tas - inte 20 olika",
      description: "Skyddar din kreditvärdighet"
    },
    {
      icon: ShieldCheck,
      title: "Godkänt av Finansinspektionen",
      value: "Lånekoll är registrerat hos Finansinspektionen",
      description: "Trygg och säker tjänst"
    },
    {
      image: creditsafeImg,
      title: "Högsta Kreditvärdighet",
      value: "AAA kreditbetyg från Creditsafe",
      description: "Bevis på stark finansiell stabilitet"
    },
    {
      image: bankTransferIcon,
      title: "Snabb Utbetalning",
      value: "1-4 bankdagar efter signering",
      description: "Pengarna på kontot snabbt"
    },
    {
      icon: Zap,
      title: "Smart App",
      value: "Full kontroll över dina lån i mobilen",
      description: "Följ ansökningar och hantera lån enkelt"
    }
  ],
  
  termsAndConditions: [
    {
      term: "Lånebelopp",
      value: "10 000 – 600 000 kr"
    },
    {
      term: "Nominell ränta",
      value: "4,95 – 22,00 %"
    },
    {
      term: "Effektiv ränta",
      value: "5,33 – 33,83 %"
    },
    {
      term: "Löptid",
      value: "1 – 15 år"
    },
    {
      term: "Uppläggningsavgift",
      value: "Varierar mellan långivare"
    },
    {
      term: "Lån utan UC",
      value: "Nej"
    },
    {
      term: "Svar på ansökan",
      value: "Samma dag"
    },
    {
      term: "Betalningslösning",
      value: "Banköverföring"
    }
  ],
  
  loanRequirements: [
    {
      requirement: "Legitimering",
      value: "Nej"
    },
    {
      requirement: "Folkbokförd i Sverige",
      value: "Ja, minst 1 år"
    },
    {
      requirement: "Svenskt personnummer",
      value: "Ja"
    },
    {
      requirement: "Svenskt mobilnummer",
      value: "Ja"
    },
    {
      requirement: "Svenskt kontonummer",
      value: "Nej"
    },
    {
      requirement: "Minsta årsinkomst",
      value: "120 000 kr"
    },
    {
      requirement: "Betalningsanmärkning OK?",
      value: "Ja"
    },
    {
      requirement: "Ålderskrav",
      value: "18 +"
    }
  ],
  
  interestRateSection: {
    title: "Lånekolls ränta",
    description: "Eftersom Lånekoll är en låneförmedlare bestäms räntan av deras samarbetspartners, inte av Lånekoll själva. När du ansöker gör långivarna en individuell bedömning baserat på UC-kreditupplysning, din betalningsförmåga och kreditrisk.",
    rateTable: [
      {
        label: "Lånebelopp (lägsta – högsta)",
        value: "10 000 – 600 000 kr"
      },
      {
        label: "Nominell ränta",
        value: "4,95 – 22,00 %"
      },
      {
        label: "Effektiv ränta",
        value: "5,33 – 33,83 %"
      },
      {
        label: "Uppläggningsavgift",
        value: "Varierar mellan långivare"
      },
      {
        label: "Aviavgift",
        value: "Varierar mellan långivare"
      },
      {
        label: "Förtidsinlösen",
        value: "Kostnadsfri"
      }
    ]
  },
  
  payoutSection: {
    title: "Utbetalning",
    description: "Utbetalningstiden varierar beroende på vilken långivare som godkänner ditt lån hos Lånekoll.",
    payoutTable: [
      {
        timeframe: "Beviljad före 12:00",
        duration: "1-4 dagar"
      },
      {
        timeframe: "Beviljad efter 12:00",
        duration: "1-4 dagar"
      }
    ]
  },
  
  faq: [
    {
      question: "Accepterar långivarna hos Lånekoll betalningsanmärkningar?",
      answer: "Ja, vissa av Lånekolls samarbetspartners kan acceptera betalningsanmärkningar om din nuvarande betalningsförmåga bedöms som god. Godkännande varierar mellan långivare och beror på din ekonomiska situation."
    },
    {
      question: "Kan man låna utan UC via Lånekoll?",
      answer: "Nej, Lånekoll gör alltid en UC-kreditupplysning när du ansöker om lån. Dock behöver du endast göra en ansökan för att få erbjudanden från flera långivare, vilket minimerar påverkan på din UC-score jämfört med att ansöka hos varje långivare separat."
    },
    {
      question: "När betalas lånet ut?",
      answer: "Normalt tar utbetalning 1-4 bankdagar efter att du signerat låneavtalet. Exakt tid beror på vilken långivare som godkänner ditt lån."
    },
    {
      question: "Kostar det något att använda Lånekoll?",
      answer: "Nej, Lånekolls tjänst är helt gratis för dig. De får provision från långivaren du väljer. Eventuella avgifter hos långivaren kan tillkomma."
    },
    {
      question: "Vilka typer av lån jämför Lånekoll?",
      answer: "Lånekoll jämför privatlån och bolån från 20 långivare. Blancolånen är utan säkerhet upp till 600 000 kr, oavsett syfte."
    },
    {
      question: "Kan man samla sina lån hos Lånekoll?",
      answer: "Ja, genom att jämföra erbjudanden från flera långivare hjälper Lånekoll dig samla lån. Detta kan ge bättre översikt och lägre räntekostnader."
    },
    {
      question: "Vilken ränta kan man få genom Lånekoll?",
      answer: "Effektiv ränta är 5,33-33,83% beroende på kreditvärdighet, lånebelopp och återbetalningstid. Långivarna sätter individuell ränta baserat på din ekonomi."
    },
    {
      question: "Om jag har A-kassa, kan jag ansöka om privatlån via Lånekoll?",
      answer: "Vissa långivare hos Lånekoll kan acceptera A-kassa som inkomst, men det påverkar ofta villkor och ränta. Ansök för att se vilka erbjudanden du får."
    },
    {
      question: "Vad är Lånekolls smarta app?",
      answer: "Med Lånekolls app får du full kontroll över dina lån. Följ ansökningar, jämför erbjudanden och hantera lån direkt i mobilen."
    },
    {
      question: "Vilka är grundkraven för att ta ett privatlån?",
      answer: "Du måste vara minst 18 år, folkbokförd i Sverige minst 1 år, ha svenskt personnummer och mobilnummer, årsinkomst minst 120 000 kr, samt vara skuldfri hos Kronofogden. Max lånebelopp är 600 000 kr."
    }
  ],
  
  customerService: {
    description: "Lånekoll erbjuder kundservice via telefon och e-post för att hjälpa dig med frågor om din ansökan, lån och deras tjänster.",
    phone: "0770-221222",
    phoneHours: [
      "Vardagar: 08:30-17:30",
      "Lördag: 08:30-15:00",
      "Söndag: Stängt"
    ],
    email: "kundservice@lanekoll.se",
    emailHours: [
      "Svarar inom 24 timmar vardagar"
    ]
  },
  
  companyInfo: {
    name: "Consector AB",
    orgNumber: "556961-4216",
    address: "Narvavägen 12",
    city: "115 22 Stockholm",
    website: "https://www.lanekoll.se",
    fiRegisterUrl: "https://www.fi.se/sv/vara-register/foretagsregistret/details?id=126514"
  },
  
  affiliateLink: {
    linkId: "lanekoll-privatlan",
    trackingSource: "provider-page",
    url: "https://www.lanekoll.se/privatlan"
  },
  
  seo: {
    title: "Lånekoll Privatlån - Jämför 20 långivare | Svar samma dag",
    description: "Lånekoll hjälper dig att hitta rätt privatlån, oavsett om du behöver ett nytt lån eller vill samla dina befintliga. Jämför privatlån från upp till 20 långivare och få full överblick i deras smarta app. Även hjälp med bolån.",
    keywords: [
      "lånekoll",
      "jämför lån",
      "privatlån",
      "bolån",
      "låneförmedlare",
      "20 långivare",
      "lånekoll recension"
    ]
  },
  
  schema: {
    productName: "Lånekoll Privatlån - Jämför 20 långivare",
    description: "Lånekoll hjälper dig hitta rätt privatlån eller samla befintliga lån. Jämför upp till 20 långivare med en ansökan. Full överblick i smarta appen. Även hjälp med bolån.",
    url: "https://klarfinans.se/sv-SE/privatlan/lanekoll",
    provider: "Lånekoll",
    interestRate: {
      min: 5.33,
      max: 33.83
    },
    minAmount: 10000,
    maxAmount: 600000,
    loanTerm: ["1 år", "15 år"],
    featureList: [
      "Jämför 20 långivares räntor med en ansökan",
      "Smart app för bättre kontroll",
      "Lån från 10 000 kr upp till 600 000 kr",
      "Jämför både privatlån och bolån",
      "Svar samma dag",
      "Effektiv ränta 5,33 – 33,83 %",
      "Endast 1 kreditupplysning tas",
      "Kan acceptera betalningsanmärkningar hos vissa långivare"
    ],
    aggregateRating: {
      ratingValue: 4.9,
      reviewCount: 1800
    }
  }
};
